import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AlpacaPortfolio.css';

const AlpacaPortfolio = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);

  const { token, logout } = useAuth();

  // Charger la liste des configurations disponibles
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch('/api/alpaca/config/all', { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data && data.data.length > 0) {
            setConfigs(data.data);
            // Sélectionner la première config par défaut
            if (!selectedConfigId && data.data[0]) {
              setSelectedConfigId(data.data[0].id);
            }
          }
        }
      } catch (e) {
        console.error('Erreur lors du chargement des configurations:', e);
      }
    };

    if (token) {
      loadConfigs();
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedConfigId && configs.length === 0) {
        // Attendre que les configs soient chargées
        return;
      }

      try {
        setLoading(true);

        const commonHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Construire les URLs avec config_id si disponible
        const portfolioUrl = selectedConfigId 
          ? `/api/alpaca/portfolio?config_id=${selectedConfigId}`
          : '/api/alpaca/portfolio';
        const ordersUrl = selectedConfigId
          ? `/api/alpaca/orders/open?config_id=${selectedConfigId}`
          : '/api/alpaca/orders/open';
        const botStatsUrl = selectedConfigId
          ? `/api/alpaca/bot/stats?config_id=${selectedConfigId}`
          : '/api/alpaca/bot/stats';

        const [portfolioRes, ordersRes, botStatsRes] = await Promise.all([
          fetch(portfolioUrl, { headers: commonHeaders }),
          fetch(ordersUrl, { headers: commonHeaders }),
          fetch(botStatsUrl, { headers: commonHeaders })
        ]);

        if (portfolioRes.status === 401) {
          let msg = 'Token invalide ou expiré';
          try { const j = await portfolioRes.json(); msg = j.message || msg; } catch {}
          setError(msg);
          await logout();
          return;
        }
        if (!portfolioRes.ok) {
          let msg = 'Erreur lors du chargement du portefeuille';
          try {
            const j = await portfolioRes.json();
            msg = j.message || j.error || msg;
          } catch {}
          throw new Error(msg);
        }
        if (ordersRes.status === 401) {
          let msg = 'Token invalide ou expiré';
          try { const j = await ordersRes.json(); msg = j.message || msg; } catch {}
          setError(msg);
          await logout();
          return;
        }
        if (!ordersRes.ok) {
          let msg = 'Erreur lors du chargement des ordres';
          try {
            const j = await ordersRes.json();
            msg = j.message || j.error || msg;
          } catch {}
          throw new Error(msg);
        }

        const portfolio = await portfolioRes.json();
        const orders = await ordersRes.json();
        
        // Récupérer les transactions depuis les stats du bot (si disponible)
        let transactionsData = [];
        if (botStatsRes.ok) {
          try {
            const botStats = await botStatsRes.json();
            // L'endpoint retourne { success: true, data: { recent_transactions: [...] } }
            if (botStats.success && botStats.data && botStats.data.recent_transactions && Array.isArray(botStats.data.recent_transactions)) {
              transactionsData = botStats.data.recent_transactions;
            } else if (botStats.recent_transactions && Array.isArray(botStats.recent_transactions)) {
              // Fallback pour l'ancien format
              transactionsData = botStats.recent_transactions;
            }
          } catch (e) {
            console.error('[AlpacaPortfolio] Error parsing bot stats:', e);
          }
        }

        setAccount(portfolio.account || null);
        setPositions(portfolio.positions || []);
        setOpenOrders(orders || []);
        setTransactions(transactionsData);
        setError(null);
      } catch (e) {
        setError(e.message || 'Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, logout, selectedConfigId, configs.length]);

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <h1 className="portfolio-title">💼 Portefeuille</h1>
        <p className="portfolio-subtitle">Compte Alpaca: valeurs, positions et ordres ouverts</p>
        {configs.length > 0 && (
          <div className="portfolio-config-selector">
            <label htmlFor="config-select">Sous-compte:</label>
            <select
              id="config-select"
              value={selectedConfigId || ''}
              onChange={(e) => setSelectedConfigId(e.target.value ? parseInt(e.target.value) : null)}
            >
              {configs.map((config) => (
                <option key={config.id} value={config.id}>
                  {config.sub_account_name || 'Sans nom'} ({config.account_type === 'live' ? 'Live' : 'Paper'})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement du portefeuille...</p>
        </div>
      ) : (
        !error && (
          <>
            {account && (
              <div className="cards-grid">
                <div className="card">
                  <h3>Valeur du portefeuille</h3>
                  <p>{account.portfolio_value}</p>
                </div>
                <div className="card">
                  <h3>Liquidité</h3>
                  <p>{account.cash}</p>
                </div>
                <div className="card">
                  <h3>Equity</h3>
                  <p>{account.equity}</p>
                </div>
              </div>
            )}

            <div className="section">
              <h2>Positions</h2>
              <div className="section-body">
                {positions.length === 0 ? (
                  <p>Aucune position.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Symbole</th>
                          <th>Qté</th>
                          <th>Prix moyen</th>
                          <th>Valeur</th>
                          <th>PNL non réalisé</th>
                        </tr>
                      </thead>
                      <tbody>
                        {positions.map((p) => (
                          <tr key={p.asset_id || p.symbol}>
                            <td>{p.symbol}</td>
                            <td>{p.qty}</td>
                            <td>{p.avg_entry_price}</td>
                            <td>{p.market_value}</td>
                            <td>{p.unrealized_pl}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="section">
              <h2>Ordres ouverts</h2>
              <div className="section-body">
                {openOrders.length === 0 ? (
                  <p>Aucun ordre ouvert.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Symbole</th>
                          <th>Qté</th>
                          <th>Type</th>
                          <th>Side</th>
                          <th>Statut</th>
                          <th>Créé</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openOrders.map((o) => (
                          <tr key={o.id}>
                            <td>{o.id}</td>
                            <td>{o.symbol}</td>
                            <td>{o.qty}</td>
                            <td>{o.type}</td>
                            <td>{o.side}</td>
                            <td>{o.status}</td>
                            <td>{o.created_at}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="section">
              <h2>📋 Historique des transactions</h2>
              <div className="section-body">
                {transactions.length === 0 ? (
                  <p>Aucune transaction enregistrée.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Symbole</th>
                          <th>Action</th>
                          <th>Quantité</th>
                          <th>Prix</th>
                          <th>Montant</th>
                          <th>Raison</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((t, index) => {
                          const dateStr = t.filled_at || t.created_at || '-';
                          const date = dateStr !== '-' ? new Date(dateStr) : null;
                          const formattedDate = date ? date.toLocaleString('fr-FR') : '-';
                          
                          return (
                            <tr key={t.alpaca_order_id || t.id || index}>
                              <td>{formattedDate}</td>
                              <td><strong>{t.symbol}</strong></td>
                              <td>
                                <span className={`action-badge ${t.action}`}>
                                  {t.action === 'buy' ? '📈 Achat' : '📉 Vente'}
                                </span>
                              </td>
                              <td>{parseFloat(t.quantity || 0).toFixed(4)}</td>
                              <td>${parseFloat(t.price || 0).toFixed(2)}</td>
                              <td>${parseFloat(t.total_amount || 0).toFixed(2)}</td>
                              <td>{t.reason || '-'}</td>
                              <td>
                                <span className={`status-badge status-${t.status || 'pending'}`}>
                                  {t.status === 'filled' || t.status === 'partially_filled' ? '✅ Rempli' : 
                                   t.status === 'new' || t.status === 'pending' || t.status === 'pending_new' || t.status === 'accepted' || t.status === 'pending_replace' ? '⏳ En attente' : 
                                   t.status === 'canceled' || t.status === 'expired' || t.status === 'rejected' ? '❌ Échoué' :
                                   '⏳ ' + (t.status || 'En attente')}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default AlpacaPortfolio;


